import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaOrg from "@/components/feature/SchemaOrg";
import AnimeCard from "@/components/base/AnimeCard";
import { animeList, categories } from "@/mocks/animeData";

const formSubmitUrl = "http://yuanyunge.cn/";

export default function ArticlePage() {
  const { id } = useParams();
  const [selectedEpisode, setSelectedEpisode] = useState(1);
  const [showDesc, setShowDesc] = useState(false);

  const anime = animeList.find((a) => a.id === id);

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">作品未找到</h1>
            <p className="text-foreground-500 mt-2">该动漫或影视作品不存在，请返回首页浏览。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer formUrl={formSubmitUrl} />
      </div>
    );
  }

  const related = animeList
    .filter(
      (a) =>
        a.id !== anime.id &&
        (a.category === anime.category || a.tags.some((t) => anime.tags.includes(t)))
    )
    .slice(0, 6);

  const categorySlug = categories.find((c) => c.name === anime.category)?.slug || "";

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: anime.category, href: categorySlug ? `/category/${categorySlug}` : undefined },
    { label: anime.title },
  ];

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: anime.title,
    description: anime.description,
    image: anime.coverImage,
    datePublished: "2026-01-01",
    dateModified: anime.updateTime,
    author: {
      "@type": "Organization",
      name: "樱花动漫网",
    },
    publisher: {
      "@type": "Organization",
      name: "樱花动漫网",
      logo: {
        "@type": "ImageObject",
        url: "http://yuanyunge.cn/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `http://yuanyunge.cn/article/${anime.id}`,
    },
    inLanguage: "zh-CN",
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <SchemaOrg type="Article" data={articleJsonLd} />
        <Breadcrumb items={breadcrumbItems} />

        <section className="py-6 md:py-10">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Cover + Info */}
              <div className="lg:col-span-1">
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-secondary-100">
                  <img
                    src={anime.coverImage}
                    alt={anime.title}
                    title={anime.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>

                <div className="mt-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {anime.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-md whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2.5 text-sm">
                    <div className="flex items-center gap-1.5 text-foreground-600">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      <span>评分 {anime.rating}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-foreground-600">
                      <i className="ri-eye-line text-xs"></i>
                      <span>{anime.views}次播放</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-foreground-600">
                      <i className="ri-calendar-line text-xs"></i>
                      <span>{anime.updateTime} 更新</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-foreground-600">
                      <i className="ri-folder-line text-xs"></i>
                      <span>{anime.category}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Info + Episodes */}
              <div className="lg:col-span-2">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  {anime.title}
                </h1>

                <div className="flex items-center gap-3 mt-3">
                  <span className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full">
                    {anime.episode}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-accent-600">
                    <i className="ri-star-fill text-xs"></i>
                    {anime.rating}
                  </span>
                </div>

                <div className="mt-5">
                  <div className="relative">
                    <p
                      className={`text-sm text-foreground-600 leading-relaxed ${
                        showDesc ? "" : "line-clamp-3"
                      }`}
                    >
                      {anime.description}
                    </p>
                    {anime.description.length > 80 && (
                      <button
                        onClick={() => setShowDesc(!showDesc)}
                        className="text-sm text-primary-500 hover:text-primary-600 mt-1 cursor-pointer font-medium"
                      >
                        {showDesc ? "收起" : "展开更多"}
                        <i
                          className={`ri-arrow-down-s-line ml-0.5 inline-block transition-transform ${
                            showDesc ? "rotate-180" : ""
                          }`}
                        ></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* Play Button */}
                <div className="flex items-center gap-3 mt-6">
                  <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-play-fill text-lg"></i>
                    立即播放 第{selectedEpisode}集
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer">
                    <i className="ri-share-line text-lg"></i>
                  </button>
                </div>

                {/* Episodes */}
                {anime.episodes.length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-lg font-bold text-foreground-950 mb-4">剧集列表</h2>
                    <div className="flex flex-wrap gap-2">
                      {anime.episodes.map((ep) => (
                        <button
                          key={ep.num}
                          onClick={() => setSelectedEpisode(ep.num)}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                            selectedEpisode === ep.num
                              ? "bg-primary-500 text-white"
                              : "bg-background-100 text-foreground-700 hover:bg-background-200"
                          }`}
                        >
                          {ep.num}
                        </button>
                      ))}
                    </div>
                    {selectedEpisode > 0 && anime.episodes.find((e) => e.num === selectedEpisode) && (
                      <p className="text-sm text-foreground-500 mt-3">
                        第{selectedEpisode}集：
                        {anime.episodes.find((e) => e.num === selectedEpisode)?.title}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div className="mt-14">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {related.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer formUrl={formSubmitUrl} />
    </div>
  );
}